import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Typography,
} from '@material-ui/core';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Breadcrumb, FormFlow } from '../../Components';
import DocumentPreviewSolicitacao from '../../Components/DocumentPreviewSolicitacao';
import SignForm from '../../Components/SignForm';
import { FluxoFlow, Formulario } from '../../interfaces/form-flow';
import {
  getAnswerForm,
  getFluxoFlowBySlug,
  getFormBySlugFluxo,
  getPreVisualizarDocumentosSolicitacao,
  startRequestFlow,
} from '../../services/form-flow';
import getCookie from '../../utils/getCookies';
import { setCookie } from '../../utils/setCookie';
import RequestModal from './components/RequestModal';
import StepperCard from './components/StepperCard';
import useStyles from './styles';

type EtapaFluxo = 'formulario' | 'preview' | 'assinatura';

export default function TempForm(): JSX.Element {
  const history = useHistory();
  const { slug } = useParams<{ slug: string }>();
  const [fluxoFlow, setFluxoFlow] = useState<FluxoFlow>();
  const [forms, setForms] = useState<Formulario[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasFormStarted, setHasFormStart] = useState<boolean>(false);
  const [formStartedMessage, setFormStartedMessage] = useState<string>('');
  const [formsAnswer, setFormsAnswer] = useState<any[]>([]);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [etapa, setEtapa] = useState<EtapaFluxo>('formulario');
  const [showModalResult, setShowModalResult] = useState<boolean>(true);
  const [requestMyData, setRequestMyData] = useState<boolean | undefined>(
    undefined,
  );
  const [hasPreviewDocs, setHasPreviewDocs] = useState<boolean>(false);
  const [loadingPreviewCheck, setLoadingPreviewCheck] = useState<boolean>(false);

  const classes = useStyles();

  const linksBreadcrumb = [
    {
      name: `${fluxoFlow?.titulo || ''} `,
      redirectTo: '#',
    },
  ];

  const getAnswers = useCallback(async () => {
    const user = JSON.parse(getCookie('gov_user_data') || '{}');
    const formStarted = JSON.parse(getCookie('gov_user_form_started') || '{}');
    setLoading(true);
    try {
      if (user.cpf && formStarted.identificador) {
        const { data } = await getAnswerForm({
          identificador: formStarted.identificador,
          cpf: user.cpf,
        });
        setFormsAnswer(data.results);
        return data.results;
      } else {
        setHasFormStart(true);
        return [];
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const startFormFlow = async (slugFlow: string) => {
    const user = JSON.parse(getCookie('gov_user_data') || '{}');
    if (user.cpf) {
      try {
        setLoading(true);
        const { status, data } = await startRequestFlow(slugFlow, user.cpf);
        if (data) {
          setCookie('gov_user_form_started', JSON.stringify(data));
          if (status === 200) {
            await getAnswers();
          }
        }
      } catch (error) {
        setFormStartedMessage(error?.response?.data?.message || '');
        if (error?.response?.status === 406) {
          setHasFormStart(true);
          document.cookie =
            'gov_user_form_started=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          await getAnswers();
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleModal = useCallback(
    () => setShowModalResult(!showModalResult),
    [showModalResult],
  );

  const getFluxoFlow = async () => {
    setLoading(true);
    try {
      const { data } = await getFluxoFlowBySlug(slug);
      if (data.results && data.results[0]) {
        setFluxoFlow(data.results[0]);

        if (!data.results[0].terceiros) {
          setRequestMyData(true);
          handleModal();
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const getFormsFlow = async () => {
    setLoading(true);
    try {
      const { data } = await getFormBySlugFluxo(slug);
      setForms(data.results);
    } finally {
      setLoading(false);
    }
  };

  const orderedForms = useMemo(() => {
    return [...forms].sort((a, b) => {
      if (a.ordem < b.ordem) return -1;
      if (a.ordem > b.ordem) return 1;
      return 0;
    });
  }, [forms]);

  const formsReady = orderedForms.length > 0;
  const previewStepIndex = orderedForms.length;
  const signStepIndex = orderedForms.length + (hasPreviewDocs ? 1 : 0);

  const handleStep = (step: number) => setActiveStep(step);

  const clearFormFlowSession = useCallback(() => {
    document.cookie =
      'gov_user_form_started=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie =
      'form-flow-session-all-values=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    try {
      const keysToRemove: string[] = [];
      for (let index = 0; index < window.localStorage.length; index += 1) {
        const key = window.localStorage.key(index);
        if (
          key &&
          (key.startsWith('form-flow-values') ||
            key.startsWith('form-flow-files'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => window.localStorage.removeItem(key));
    } catch (error) {
      console.warn('Falha ao limpar armazenamento do formulário', error);
    }
  }, []);

  const handleSignSuccess = useCallback(() => {
    clearFormFlowSession();
  }, [clearFormFlowSession]);

  const irParaAssinatura = useCallback(() => {
    setEtapa('assinatura');
    setActiveStep(signStepIndex);
  }, [signStepIndex]);

  const irParaPreview = useCallback(() => {
    setHasPreviewDocs(true);
    setEtapa('preview');
    setActiveStep(previewStepIndex);
  }, [previewStepIndex]);

  const handleFormSubmit = useCallback(async () => {
    const formStarted = JSON.parse(getCookie('gov_user_form_started') || '{}');
    const solicitacaoId = formStarted?.id;
    if (!solicitacaoId) {
      setHasPreviewDocs(false);
      setEtapa('assinatura');
      setActiveStep(orderedForms.length);
      return;
    }
    try {
      setLoadingPreviewCheck(true);
      const { data } = await getPreVisualizarDocumentosSolicitacao(solicitacaoId);
      const results = data?.results || [];
      if (results.length > 0) {
        irParaPreview();
        return;
      }
      setHasPreviewDocs(false);
      setEtapa('assinatura');
      setActiveStep(orderedForms.length);
    } catch (error) {
      console.error(error);
      setHasPreviewDocs(false);
      setEtapa('assinatura');
      setActiveStep(orderedForms.length);
    } finally {
      setLoadingPreviewCheck(false);
    }
  }, [irParaPreview, orderedForms.length]);

  const handleBackFromPreview = useCallback(() => {
    setEtapa('formulario');
    setActiveStep(Math.max(orderedForms.length - 1, 0));
  }, [orderedForms.length]);

  useEffect(() => {
    getFluxoFlow();
    getFormsFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (fluxoFlow) {
      startFormFlow(fluxoFlow.slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fluxoFlow]);

  const stepperActiveStep = hasFormStarted
    ? signStepIndex + 1
    : etapa === 'assinatura'
      ? signStepIndex
      : etapa === 'preview'
        ? previewStepIndex
        : activeStep;

  return (
    <>
      <Breadcrumb links={linksBreadcrumb} />
      <Container>
        <RequestModal
          onClose={handleModal}
          open={showModalResult}
          isForMy={requestMyData}
          setIsForMy={setRequestMyData}
        />
        <Grid container>
          <Grid item xs={12}>
            <Box padding={2}>
              {fluxoFlow && (
                <Typography className={classes.title}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(fluxoFlow.titulo),
                    }}
                  />
                </Typography>
              )}
              {fluxoFlow && (
                <Typography className={classes.description}>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(fluxoFlow.descricao),
                    }}
                  />
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
        <Grid container alignItems='center' justifyContent='center' spacing={2}>
          <Grid item xs={12}>
            <Card className={classes.mainCard}>
              <CardContent className={classes.cardContent}>
                {loading || !formsReady || loadingPreviewCheck ? (
                  <Box className={classes.boxContent}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box className={classes.boxContent}>
                    <StepperCard
                      itens={orderedForms}
                      activeStep={stepperActiveStep}
                      includePreviewStep={hasPreviewDocs || etapa === 'preview'}
                    />
                    {!hasFormStarted && requestMyData !== undefined ? (
                      <>
                        {etapa === 'formulario' && (
                          <FormFlow
                            formData={orderedForms}
                            handleStep={step => {
                              setEtapa('formulario');
                              handleStep(step);
                            }}
                            page={activeStep}
                            handleSubmit={handleFormSubmit}
                            formsAnswerData={formsAnswer}
                            requestMyData={requestMyData}
                            flowPreviewEnabled={false}
                            skipAdvanceStepOnLastSave
                          />
                        )}
                        {etapa === 'preview' && (
                          <DocumentPreviewSolicitacao
                            onContinue={irParaAssinatura}
                            onBack={handleBackFromPreview}
                          />
                        )}
                        {etapa === 'assinatura' && (
                          <SignForm
                            handleStep={step => {
                              if (hasPreviewDocs && step >= previewStepIndex) {
                                setEtapa('preview');
                                setActiveStep(previewStepIndex);
                                return;
                              }
                              setEtapa('formulario');
                              handleStep(Math.min(step, Math.max(orderedForms.length - 1, 0)));
                            }}
                            page={activeStep}
                            handleSubmit={handleSignSuccess}
                            hasTerms={fluxoFlow?.termo ? fluxoFlow.termo : null}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <Typography align='center'>
                          {formStartedMessage}{' '}
                        </Typography>
                        <Button
                          className={classes.buttonForm}
                          variant='contained'
                          fullWidth
                          color='primary'
                          type='submit'
                          style={{ marginTop: 20 }}
                          onClick={() =>
                            history.push('/workspace/minhas_solicitacoes')
                          }
                        >
                          Minhas solicitações
                        </Button>
                        <Button
                          style={{ color: '#0F6FB7', width: '100%' }}
                          variant='text'
                          onClick={() => history.push('/')}
                        >
                          Home{' '}
                        </Button>
                      </>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </>
  );
}
